import { useEffect } from "react";

import { HttpError } from "@bps/http-client";
import { useFieldDeviceDeploymentRings } from "@libs/api/gateways/field/field-ops-gateway.hooks";
import {
  ApplicationType,
  Tenant
} from "@libs/api/gateways/plt/plt-gateway.dtos";
import { useTenantsPltQuery } from "@libs/api/gateways/plt/plt-gateway.hooks";
import { getPagesResult } from "@libs/paging/paging.utils";

import { DeployManagementFilterValues } from "./DeployManagementFilters";

const MIN_VISIBLE_ROWS = 25;

const TENANT_PAGE_SIZE = 500;

// The Name search filters the loaded rows client-side (by name or id); it does not hit the
// backend, so it never triggers paging.
const matchesSearch = (tenant: Tenant, term: string) =>
  tenant.name.toLowerCase().includes(term) ||
  tenant.id.toLowerCase().includes(term);

export interface DeployManagementTenantsResult {
  tenants: Tenant[];
  isLoading: boolean;
  error: HttpError | null;
  hasNextPage: boolean;
  onScrolledToBottom: () => Promise<void>;
  loadingMore: boolean;
}

export const useDeployManagementTenants = (
  filters: DeployManagementFilterValues
): DeployManagementTenantsResult => {
  const { search, deploymentRingId } = filters;

  // A deployment ring must be picked before anything loads: the whole screen is scoped to the
  // devices assigned to that ring.
  const ringFilterActive = !!deploymentRingId;

  const {
    data,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
    error
  } = useTenantsPltQuery(
    {
      isInactive: false,
      applications: [ApplicationType.PROS],
      limit: TENANT_PAGE_SIZE
    },
    { enabled: ringFilterActive }
  );

  // Pull the field devices assigned to the selected ring so we can keep only tenants whose
  // site authority device belongs to it.
  const {
    data: ringDevices,
    isLoading: ringDevicesLoading
  } = useFieldDeviceDeploymentRings(undefined, deploymentRingId || undefined, {
    enabled: ringFilterActive
  });

  const ringDeviceIds = new Set(
    (ringDevices ?? []).map(device => device.fieldDeviceId)
  );

  const tenants = getPagesResult(data);
  // Show only tenants with a field device that belongs to the selected ring — there is nothing
  // to deploy without a device, and the screen is scoped to the ring.
  const ringTenants = tenants.filter(
    tenant =>
      !!tenant.siteAuthorityFieldDeviceId &&
      ringDeviceIds.has(tenant.siteAuthorityFieldDeviceId)
  );

  const searchTerm = search.trim().toLowerCase();
  const visibleTenants = searchTerm
    ? ringTenants.filter(tenant => matchesSearch(tenant, searchTerm))
    : ringTenants;

  const ringDevicesSettling = ringFilterActive && ringDevicesLoading;

  const hasMorePages = (hasNextPage ?? false) && !isFetchingNextPage;
  // Based on ringTenants, not the searched list, so typing an unmatched term never pages.
  const viewportUnderfilled = ringTenants.length < MIN_VISIBLE_ROWS;

  // The ring filter runs client-side over loaded pages. When it hides everything, the scroll
  // listener never fires (nothing to scroll), so pull the next page here instead.
  const shouldAutoPage =
    hasMorePages && !ringDevicesSettling && viewportUnderfilled;

  useEffect(() => {
    if (shouldAutoPage) {
      fetchNextPage();
    }
  }, [shouldAutoPage, fetchNextPage]);

  const onScrolledToBottom = async () => {
    if (hasNextPage) {
      await fetchNextPage();
    }
  };

  return {
    tenants: visibleTenants,
    isLoading,
    error,
    hasNextPage: hasNextPage ?? false,
    onScrolledToBottom,
    loadingMore: isFetchingNextPage || ringDevicesSettling
  };
};
